import { PaymentFrameParameters, PaymentFrameStyle } from './iframe.types';

// Declare global function injected at runtime
declare function createPaymentFrame(
  type: 'cardnumber',
  containerSelector: string,
  parameters: PaymentFrameParameters,
  style: PaymentFrameStyle
): void;

import React, { useCallback, useEffect, useRef } from 'react';
import {
  additionalStyles,
  DS_FIELD_EVENTS,
  DS_MESSAGE_TYPES,
  DS_SOURCE,
  DS_SYSTEM_EVENTS,
  iframeInputStylesInitial,
  iframeInputStylesInvalid
} from '@trip-ux/constants';
import { useIframeEventHandler, getCardType } from './iframe.utils';

// LOCAL DEV ONLY — read the JWE from `tripx-ux/.env` (gitignored).
// Webpack DefinePlugin (tools/webpack.fe.js + tools/webpack.stateless.fe.js) inlines
// `process.env.DS_IFRAME_TOKEN` at build time for non-prod bundles only — production
// bundles use the Vaulting.io PCI proxy injection path (the `!isLocalhost` branch below)
// so this constant is empty/unused in prod.
//
// To refresh the token:
//   1. Open https://www-uat.manulife-travel.ca/__datastealth/test/parent_payment_development_demo.html
//   2. View source > find <meta name="token" content="..."> under the
//      `// manulifeaffinity-development-3004` comment (matches `npm run start:coverme` port 3004)
//   3. Set in tripx-ux/.env:
//        DS_IFRAME_TOKEN='<paste-jwe-here>'
//   4. Restart the client watcher (`npm run watch:coverme:client`) so DefinePlugin re-reads .env.
const LOCAL_DEV_DS_IFRAME_TOKEN = process.env.DS_IFRAME_TOKEN || '';

const CARD_NUMBER_STYLE = {
  iframeBorder: '1px solid #c2c3c9',
  iframeScrolling: 'no',
  iframeWidth: '100%',
  iframeHeight: '48px',
  iframeDisplay: 'block'
};

const CreditCardIFrame = ({ onMessage, id, path, iframeUrl }) => {
  const isLocalhost = globalThis.location.hostname === 'localhost' || globalThis.location.hostname === '127.0.0.1';
  const fieldIdx = parseInt(id.split('-')[1], 10) - 1;
  const containerId = `cardnumber-${fieldIdx}`;

  // Capture setup props once on mount - these should never change for component lifetime
  const setupPropsRef = useRef({ path, fieldIdx, isLocalhost, iframeUrl });

  const [handleIframeEvent] = useIframeEventHandler(
    iframeUrl,
    iframeInputStylesInitial,
    {},
    iframeInputStylesInvalid,
    additionalStyles
  );

  // Consolidated empty handlers
  const noop = useCallback(() => {}, []);
  const handleBlurFieldEvent = noop;
  const handleFocusFieldEvent = noop;

  const handleValidFieldEvent = useCallback(
    message => {
      let cardType;
      const {
        detail: cardToken,
        additional: { cardPrefix },
        target,
        index
      } = message;
      if (target === 'cardnumber') {
        cardType = getCardType(cardPrefix);
      }
      onMessage({ token: cardToken, type: cardType, hasError: false }, index + 1);
    },
    [onMessage]
  );

  const handleInvalidFieldEvent = useCallback(
    message => {
      const { index } = message;
      onMessage({ token: null, type: null, hasError: true }, index + 1);
    },
    [onMessage]
  );

  const handleFieldEvent = useCallback(
    message => {
      const { event } = message;
      switch (event) {
        case DS_FIELD_EVENTS.ON_VALID:
          handleValidFieldEvent(message);
          break;
        case DS_FIELD_EVENTS.ON_INVALID:
          handleInvalidFieldEvent(message);
          break;
        case DS_FIELD_EVENTS.ON_BLUR:
          handleBlurFieldEvent();
          break;
        case DS_FIELD_EVENTS.ON_FOCUS:
          handleFocusFieldEvent();
          break;
        default:
          break;
      }
    },
    [handleValidFieldEvent, handleInvalidFieldEvent, handleBlurFieldEvent, handleFocusFieldEvent]
  );

  const handleSystemEvent = useCallback(
    message => {
      const { index } = message;
      if (message.event === DS_SYSTEM_EVENTS.ON_ERROR) {
        onMessage({ token: null, cardType: null, hasError: true }, index + 1);
      }
    },
    [onMessage]
  );

  // Use refs to store latest handlers without triggering useEffect re-runs
  const handlersRef = useRef({ handleFieldEvent, handleSystemEvent, handleIframeEvent });
  useEffect(() => {
    handlersRef.current = { handleFieldEvent, handleSystemEvent, handleIframeEvent };
  }, [handleFieldEvent, handleSystemEvent, handleIframeEvent]);

  // Create iframe once on mount
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container || container.querySelector('iframe')) {
      return;
    }

    if (!setupPropsRef.current.isLocalhost) {
      const cardNumberParameters = {
        settingsid: `${setupPropsRef.current.path}`,
        fieldindex: setupPropsRef.current.fieldIdx,
        arialabel: `creditcard-${setupPropsRef.current.fieldIdx}`,
        title: `creditcard-${setupPropsRef.current.fieldIdx}`
      };
      createPaymentFrame('cardnumber', `#${containerId}`, cardNumberParameters, CARD_NUMBER_STYLE);
    } else {
      const iframe = document.createElement('iframe');
      iframe.id = `field-protect-${containerId}`;
      iframe.src = `${setupPropsRef.current.iframeUrl}/payment/protect/payment_frame_field.html?e=${LOCAL_DEV_DS_IFRAME_TOKEN}&fieldtype=cardnumber&fieldindex=${setupPropsRef.current.fieldIdx}`;
      iframe.style.border = '0';
      iframe.title = `credit-card-${setupPropsRef.current.fieldIdx}`;
      iframe.width = '100%';
      iframe.height = '48px';
      iframe.setAttribute('aria-label', `creditcard-${setupPropsRef.current.fieldIdx}`);
      iframe.setAttribute('scrolling', 'no');
      container.appendChild(iframe);
    }
  }, [containerId]);

  // Set up event listener once on mount
  useEffect(() => {
    const handleMessage = e => {
      let message;
      try {
        message = JSON.parse(e.data);
      } catch (error) {
        return;
      }
      const { source, type, target } = message;
      if (source !== DS_SOURCE.FRAME) {
        return;
      }
      if (target === 'cardnumber') {
        if (type === DS_MESSAGE_TYPES.FIELD) {
          handlersRef.current.handleFieldEvent(message);
        } else if (type === DS_MESSAGE_TYPES.SYSTEM) {
          handlersRef.current.handleSystemEvent(message);
        } else if (type === DS_MESSAGE_TYPES.IFRAME) {
          const iframeDoc = document.getElementById(`field-protect-${containerId}`);
          handlersRef.current.handleIframeEvent({ ...message, iframe: iframeDoc });
        }
      }
    };
    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage, false);
  }, [containerId]);

  return <div id={containerId}></div>;
};

export default CreditCardIFrame;
